import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Phone, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import usePageContent, { s, list } from "@/hooks/usePageContent";
import { localePath, parseLocaleFromPath } from "@/lib/locale";

export default function Service() {
  const { t } = useTranslation();
  const location = useLocation();
  const { locale } = parseLocaleFromPath(location.pathname);
  const { data: page } = usePageContent("service", locale);
  const { data: contactPage } = usePageContent("contact", locale);
  const services = list(page, "services");

  return (
    <>
      <SEO title={page?.meta_title} description={page?.meta_description} />
      <article className="bg-background grain-bg">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "eyebrow")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.05]">{s(page, "title")}</h1>
            <p className="mt-5 text-lg text-foreground/75">{s(page, "subtitle")}</p>
          </div>

          <div className="mt-14 grid lg:grid-cols-[1.4fr_1fr] gap-10">
            <div className="space-y-6 text-foreground/85 text-lg leading-relaxed" data-testid="service-body">
              <RichContent html={s(page, "p1")} />

              <div className="rounded-lg bg-card border border-border/60 shadow-ambient p-6 lg:p-8">
                <h2 className="font-serif text-2xl text-foreground mb-4">{s(page, "services_title")}</h2>
                <ul className="space-y-3">
                  {services.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-base text-foreground/85">
                      <Check className="h-4 w-4 text-primary mt-1 shrink-0" strokeWidth={2} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <RichContent html={s(page, "p2")} />
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg bg-tz-espresso text-tz-cream p-7 shadow-ambient">
                <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3">{s(contactPage, "eyebrow")}</div>
                <h3 className="font-serif text-2xl mb-4">{s(contactPage, "title")}</h3>
                <div className="space-y-3 text-sm">
                  <a href={`tel:${t("footer.phone_landline_tel")}`} className="flex items-center gap-3 text-tz-cream/85 hover:text-primary transition-colors">
                    <Phone className="h-4 w-4" strokeWidth={1.5} />
                    <span><span className="text-tz-cream/55 text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_landline_label")}</span>{t("footer.phone_landline")}</span>
                  </a>
                  <a href={`tel:${t("footer.phone_mobile_tel")}`} className="flex items-center gap-3 text-tz-cream/85 hover:text-primary transition-colors">
                    <Phone className="h-4 w-4" strokeWidth={1.5} />
                    <span><span className="text-tz-cream/55 text-[10px] uppercase tracking-[0.18em] mr-2">{t("footer.phone_mobile_label")}</span>{t("footer.phone_mobile")}</span>
                  </a>
                  <a href={`mailto:${t("footer.email")}`} className="flex items-center gap-3 text-tz-cream/85 hover:text-primary transition-colors break-all">
                    <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} /> {t("footer.email")}
                  </a>
                </div>
                <Link
                  to={localePath(locale, "/contact")}
                  className="mt-6 inline-flex items-center justify-center w-full rounded-lg bg-primary text-primary-foreground py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  {t("actions.contact_us")}
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}
